import { Users, Award, Globe, Target, ArrowRight, Sparkles } from "lucide-react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import aboutTeam from "@/assets/about-team.jpg";
import { useLanguage } from "@/i18n/LanguageContext";
import { translations, t } from "@/i18n/translations";

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const { language } = useLanguage();

  const team = [
    { name: "Aida Djigo Wane", role: "Directrice Générale", description: "Économiste financier sénior, +25 ans d'expérience en ingénierie financière et économique, ancienne DGA de l'APIX", specialties: ["Finance publique", "Econométrie", "Modélisation"] },
    { name: "Arfang Latif Bodian", role: "Directeur Adjoint", description: "Expert en modélisation financière et économétrie", specialties: ["Modélisateur", "Econométre", "Statisticien"] },
    { name: "Elisa Diédhiou", role: "Economiste", description: "Spécialiste en économie et modélisation financière", specialties: ["Economie et Finance"] },
    { name: "Alioune Gueye", role: "Informaticien", description: "Spécialiste en data engineering et développement d'application", specialties: ["Data Engineering", "ETL", "Dashboard", "Développement"] },
    { name: "Mame Fatou Gueye", role: "Economiste", description: "Spécialiste en économétrie et modélisation financière", specialties: ["Economie et Finance"] },
    { name: "Justine Diarra", role: "Administratrice", description: "Gestion administrative et coordination des projets", specialties: ["Administration"] }
  ];

  const valuesData = [
    { icon: Target, ...translations.about.values.excellence },
    { icon: Users, ...translations.about.values.collaboration },
    { icon: Award, ...translations.about.values.expertise },
    { icon: Globe, ...translations.about.values.innovation },
  ];

  const stats = [
    { value: "6+", label: t(translations.about.statsYears, language) },
    { value: "15+", label: t(translations.about.statsProjects, language) },
    { value: "30+", label: t(translations.about.statsClients, language) },
  ];

  return (
    <section id="about" className="relative py-28 overflow-hidden" ref={ref}>
      <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/20 to-background" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[hsl(var(--brand-red))/0.03] rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={isInView ? { scale: 1 } : {}}
            transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
            className="inline-flex items-center gap-2 bg-[hsl(var(--brand-red))/0.08] border border-[hsl(var(--brand-red))/0.15] rounded-full px-5 py-2 mb-6"
          >
            <Sparkles className="h-4 w-4 text-[hsl(var(--brand-red))]" />
            <span className="text-[hsl(var(--brand-red))] text-sm font-semibold tracking-wide uppercase">{t(translations.about.badge, language)}</span>
          </motion.div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-5">
            {t(translations.about.title, language)} <span className="bg-gradient-to-r from-[hsl(var(--brand-red))] to-[hsl(var(--brand-navy))] bg-clip-text text-transparent">{t(translations.about.titleHighlight, language)}</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t(translations.about.subtitle, language)}
          </p>
        </motion.div>

        {/* Story section */}
        <div className="grid lg:grid-cols-2 gap-16 mb-28 items-center">
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2">
              <div className="w-10 h-[2px] bg-[hsl(var(--brand-red))]" />
              <span className="text-[hsl(var(--brand-red))] font-semibold text-sm uppercase tracking-wider">{t(translations.about.storyBadge, language)}</span>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold text-foreground leading-tight">
              {t(translations.about.storyTitle, language)} <span className="text-[hsl(var(--brand-red))]">{t(translations.about.storyHighlight, language)}</span>
            </h3>
            <div className="space-y-5 text-muted-foreground leading-relaxed text-[15px]">
              <p>{t(translations.about.storyP1, language)}</p>
              <p>{t(translations.about.storyP2, language)}</p>
              <p>{t(translations.about.storyP3, language)}</p>
            </div>

            <div className="flex gap-8 pt-4">
              {stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl font-bold bg-gradient-to-r from-[hsl(var(--brand-red))] to-[hsl(var(--brand-navy))] bg-clip-text text-transparent">{stat.value}</div>
                  <div className="text-xs text-muted-foreground mt-1 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
          
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <div className="relative">
              <div className="absolute -inset-3 bg-gradient-to-br from-[hsl(var(--brand-red))/0.2] to-[hsl(var(--brand-navy))/0.2] rounded-3xl blur-xl" />
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img src={aboutTeam} alt="Notre équipe" className="w-full h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--brand-navy))/0.6] via-transparent to-transparent" />
              </div>
              <div className="absolute bottom-6 left-6 right-6 backdrop-blur-md bg-white/10 border border-white/20 rounded-xl p-4">
                <p className="text-white text-sm font-medium">{t(translations.about.imageQuote, language)}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Values */}
        <div className="mb-28">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-3">{t(translations.about.valuesTitle, language)}</h3>
            <p className="text-muted-foreground max-w-xl mx-auto">{t(translations.about.valuesSubtitle, language)}</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {valuesData.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                className="group relative overflow-hidden rounded-2xl bg-card border border-border p-8 hover:border-[hsl(var(--brand-red))/0.4] transition-all duration-500 hover:-translate-y-2 hover:shadow-xl"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--brand-red))/0.05] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-[hsl(var(--brand-red))] to-[hsl(var(--brand-navy))] rounded-2xl flex items-center justify-center mx-auto mb-5 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg">
                    <value.icon className="h-7 w-7 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-foreground mb-2">{t(value.title, language)}</h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">{t(value.desc, language)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Team — untouched */}
        <div>
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-3">{t(translations.about.teamTitle, language)}</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t(translations.about.teamSubtitle, language)}</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mb-8"
          >
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[hsl(var(--brand-navy))] to-[hsl(var(--brand-red))] p-[1px]">
              <div className="bg-card rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
                <div className="relative">
                  <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-gradient-to-br from-[hsl(var(--brand-red))] to-[hsl(var(--brand-navy))] p-[3px]">
                    <div className="w-full h-full rounded-full bg-card flex items-center justify-center">
                      <span className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-[hsl(var(--brand-red))] to-[hsl(var(--brand-navy))] bg-clip-text text-transparent">
                        {team[0].name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </span>
                    </div>
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-[hsl(var(--brand-red))] rounded-full flex items-center justify-center shadow-lg">
                    <Award className="h-4 w-4 text-white" />
                  </div>
                </div>
                <div className="text-center md:text-left flex-1">
                  <h4 className="text-xl md:text-2xl font-bold text-foreground">{team[0].name}</h4>
                  <p className="text-[hsl(var(--brand-red))] font-semibold mb-2">{team[0].role}</p>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-3">{team[0].description}</p>
                  <div className="flex flex-wrap justify-center md:justify-start gap-2">
                    {team[0].specialties.map((s, i) => (
                      <span key={i} className="text-xs px-3 py-1.5 rounded-full bg-[hsl(var(--brand-red))/0.1] text-[hsl(var(--brand-red))] font-medium">{s}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {team.slice(1).map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3 + index * 0.08, duration: 0.5 }}
                className="group relative overflow-hidden rounded-xl bg-card border border-border hover:border-[hsl(var(--brand-red))/0.3] transition-all duration-500 hover:shadow-[var(--shadow-card)] hover:-translate-y-1"
              >
                <div className="h-1.5 bg-gradient-to-r from-[hsl(var(--brand-red))] to-[hsl(var(--brand-navy))] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="p-5 flex flex-col items-center text-center space-y-3">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[hsl(var(--brand-red))/0.15] to-[hsl(var(--brand-navy))/0.15] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <span className="text-lg font-bold text-[hsl(var(--brand-red))]">
                      {member.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground group-hover:text-[hsl(var(--brand-red))] transition-colors leading-tight">{member.name}</h4>
                    <p className="text-xs font-semibold text-[hsl(var(--brand-navy))] mt-0.5">{member.role}</p>
                  </div>
                  <div className="flex flex-wrap justify-center gap-1">
                    {member.specialties.map((s, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{s}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
